import { apiClient } from '@/core/infra_api_client'
import { compareReleaseVersions, isReleaseVersion } from '@/core/infra_semver'

// ============= Types =============

export interface TerraformModule {
  terraformVersion?: string
  sshAccessPolicy?: string
  module: string       // e.g. "gcp-vm", "k3s-cluster"
  tags: string[]       // e.g. ["v0.1.0", "v0.2.0"]
  digest: string
  sizeBytes: number
  pushedAt: string
}

/** Flattened version for display — one row per module+tag combo */
export interface TerraformModuleVersion {
  module: string
  version: string
  digest: string
  sizeBytes: number
  pushedAt: string
}

// ============= API =============

export async function listTerraformModules(): Promise<TerraformModule[]> {
  return apiClient.get<TerraformModule[]>('images/terraform-modules')
}

export async function getTerraformRelease(module: string, version: string): Promise<TerraformModule> {
  return apiClient.get<TerraformModule>(`images/terraform-modules/${encodeURIComponent(module)}/${encodeURIComponent(version)}`)
}

/** Flatten modules into per-version rows, sorted newest first */
export function flattenModuleVersions(modules: TerraformModule[]): TerraformModuleVersion[] {
  const versions: TerraformModuleVersion[] = []
  for (const mod of modules) {
    for (const tag of mod.tags) {
      versions.push({
        module: mod.module,
        version: tag,
        digest: mod.digest,
        sizeBytes: mod.sizeBytes,
        pushedAt: mod.pushedAt,
      })
    }
  }
  // Sort by module name, then version descending
  return versions.sort((a, b) => {
    if (a.module !== b.module) return a.module.localeCompare(b.module)
    return compareReleaseVersions(a.version, b.version)
  })
}

/** Get versions for a specific module, sorted newest first */
export function getModuleVersions(modules: TerraformModule[], moduleName: string): string[] {
  return [...new Set(modules.filter(m => m.module === moduleName).flatMap(m => m.tags))]
    .filter(isReleaseVersion).sort(compareReleaseVersions)
}
